// Story 8.5 (ADO-2014), D-11, D-15 and D-19: the shared cut-off read. Zustand per ADR-004.
//
// One answer per cart, shared by every consumer, so the header chip, the cart banner and
// any other pill on screen cost one `GET /basket/cut-off-status` and one set of refetch
// timers per cart, not one per mount. Consumers read it through `useCutOffStatus` (or
// `useDepotCutOff`), never directly. Story 8.4 (ADO-2013), 8.5 D-20: a cart is the depot AND
// the Trading Account (`CartAccount`, keyed by `cartAccountKey`), since each Value Centre account
// has its own cut-off; the "depot" below is that cart. A cart with no account chosen has the
// depot id as its key. Enabler E8-G (ADO-3109), DA-8: the cut-off is not keyed by the range, so
// Chill and Fruit & Veg at depot 25, two carts, share one read (SL1.0 reads one delivery schedule
// for both: `engine/get_smanager.php:5563-5565,5673-5676`).
//
// Behaviour:
//   - `subscribeToCartCutOff(cart)` is ref-counted. The first subscriber for a cart
//     fetches; later ones reuse the answer (or the read in flight).
//   - When the last subscriber leaves, the teardown is deferred by one tick (`setTimeout(0)`):
//     routes are flat and every screen mounts its own header, so on a navigation the old
//     screen's consumers leave before the new screen's join in the same commit (and
//     `StrictMode` unmounts and remounts once). A subscriber that joins the same depot
//     before the tick cancels the teardown and keeps everything as it was: the answer, the
//     timers, the retry count and any read in flight, so there is no flicker and no extra
//     read. Otherwise, one tick later, the read in flight is aborted, every timer (refetch
//     and retry) is cleared and the answer is dropped, so a later mount reads afresh. A
//     response for a depot whose teardown has run is never stored.
//   - It refetches once when the displayed deadline is reached (so OPEN becomes PASSED or
//     the next OPEN), and once at each Dublin midnight while a cut-off is shown. No polling.
//     A same-depot refetch keeps the last answer up until the new one lands.
//   - A failed read with no answer shown (the first read for a depot) hides the pill, and
//     the next consumer to join reads again, as a fresh mount always did.
//   - A failed refetch while an answer is shown (D-19) keeps that answer up (after the
//     deadline it reads "0m 00s", the deadline reached) and the midnight timer armed, and
//     retries after 5s, 15s and then 60s. A successful retry stores the answer and re-arms
//     as usual. If all three fail, the answer is dropped (hidden) and only the midnight timer
//     stays armed, so the midnight read can bring it back; a later consumer can also read.
//   - `offsetMs` is `serverNow - clientReceiptTime`: add it to the client clock to get the
//     server's (the countdown is advisory, the server is authoritative).
//   - The clock is the first subscriber's (`Date.now` unless a spec injects one).

import { create } from 'zustand';
import { getCutOffStatus, type CutOffStatus } from '../api/basket.client';
import { cartAccountKey, type CartAccount } from '../api/cart-target';
import { msUntilNextLocalMidnight } from '../shared/cutOffDisplay';

export interface CutOffStatusView {
  readonly status: CutOffStatus;
  /** Add to the client clock to read the server's. */
  readonly offsetMs: number;
}

interface CutOffState {
  /** The latest answer per cart key (`cartAccountKey`), for the carts someone subscribes to. */
  readonly views: Readonly<Record<string, CutOffStatusView>>;
}

export const useCutOffStore = create<CutOffState>(() => ({ views: {} }));

/** Past the inclusive deadline (and the rollover) before asking the server again. */
const REFETCH_GRACE_MS = 500;
/** D-19: the waits before each retry of a failed refetch; its length is the retry bound. */
const REFETCH_RETRY_DELAYS_MS = [5_000, 15_000, 60_000] as const;

type Timer = ReturnType<typeof setTimeout>;

interface CartSubscription {
  /** The cart this subscription reads. */
  readonly cart: CartAccount;
  subscriberCount: number;
  readonly now: () => number;
  controller: AbortController | null;
  deadlineTimer: Timer | null;
  midnightTimer: Timer | null;
  retryTimer: Timer | null;
  /** The last subscriber's deferred teardown, cancelled if a subscriber joins first. */
  teardownTimer: Timer | null;
  /** Retries of a failed refetch already scheduled since the last answer. */
  retriesUsed: number;
  /** The last answer's clock offset, to place the midnight timer after a failure. */
  offsetMs: number;
  /** The last read failed with nothing shown: the next subscriber to join reads again. */
  failed: boolean;
}

const subscriptions = new Map<string, CartSubscription>();
// Per cart, the deadline already refetched for, so a lagging server that answers the same
// OPEN again does not loop. Per cart because two carts can share one cut-off instant and
// a Shopper can switch back and forth between them; it outlives the subscription for that.
const refetchedDeadlineByCart = new Map<string, string>();

function setView(cartKey: string, view: CutOffStatusView | null): void {
  useCutOffStore.setState((state) => {
    if (view === null && !(cartKey in state.views)) return state;
    const views = { ...state.views };
    if (view === null) delete views[cartKey];
    else views[cartKey] = view;
    return { views };
  });
}

function clearTimer(timer: Timer | null): null {
  if (timer !== null) clearTimeout(timer);
  return null;
}

function disarm(subscription: CartSubscription): void {
  subscription.deadlineTimer = clearTimer(subscription.deadlineTimer);
  subscription.midnightTimer = clearTimer(subscription.midnightTimer);
  subscription.retryTimer = clearTimer(subscription.retryTimer);
}

function isCurrent(cartKey: string, subscription: CartSubscription): boolean {
  return subscriptions.get(cartKey) === subscription;
}

function refetchIfCurrent(cartKey: string, subscription: CartSubscription): void {
  if (isCurrent(cartKey, subscription)) read(cartKey, subscription);
}

function read(cartKey: string, subscription: CartSubscription): void {
  subscription.controller?.abort();
  const controller = new AbortController();
  subscription.controller = controller;
  subscription.failed = false;
  getCutOffStatus(subscription.cart, { signal: controller.signal })
    .then((status) => {
      if (controller.signal.aborted || !isCurrent(cartKey, subscription)) return;
      const offsetMs =
        status.state === 'NONE' ? 0 : Date.parse(status.serverNow) - subscription.now();
      subscription.retriesUsed = 0;
      subscription.offsetMs = offsetMs;
      setView(cartKey, { status, offsetMs });
      arm(cartKey, subscription, status, offsetMs);
    })
    .catch(() => {
      if (controller.signal.aborted || !isCurrent(cartKey, subscription)) return;
      onReadFailed(cartKey, subscription);
    });
}

function onReadFailed(cartKey: string, subscription: CartSubscription): void {
  subscription.retryTimer = clearTimer(subscription.retryTimer);
  const shown = cartKey in useCutOffStore.getState().views;
  const retryDelayMs = REFETCH_RETRY_DELAYS_MS[subscription.retriesUsed];
  if (shown && retryDelayMs !== undefined) {
    // D-19: keep the answer up and the midnight timer armed, and retry.
    subscription.retriesUsed += 1;
    armMidnightIfIdle(cartKey, subscription);
    subscription.retryTimer = setTimeout(() => {
      subscription.retryTimer = null;
      refetchIfCurrent(cartKey, subscription);
    }, retryDelayMs);
    return;
  }
  // Nothing shown (the first read), or every retry failed: hide. After the retries the
  // midnight timer stays armed so the midnight read can bring the answer back; a failed
  // first read (or a failed midnight read after the retries) has none left.
  subscription.deadlineTimer = clearTimer(subscription.deadlineTimer);
  if (shown) armMidnightIfIdle(cartKey, subscription);
  subscription.retriesUsed = 0;
  subscription.failed = true;
  setView(cartKey, null);
}

function armMidnightIfIdle(cartKey: string, subscription: CartSubscription): void {
  if (subscription.midnightTimer !== null) return;
  const serverNow = subscription.now() + subscription.offsetMs;
  subscription.midnightTimer = setTimeout(
    () => {
      subscription.midnightTimer = null;
      refetchIfCurrent(cartKey, subscription);
    },
    msUntilNextLocalMidnight(serverNow) + REFETCH_GRACE_MS,
  );
}

function arm(
  cartKey: string,
  subscription: CartSubscription,
  status: CutOffStatus,
  offsetMs: number,
): void {
  disarm(subscription);
  if (status.state === 'NONE') return;
  const serverNow = subscription.now() + offsetMs;
  if (status.state === 'OPEN' && refetchedDeadlineByCart.get(cartKey) !== status.deadline) {
    const msToDeadline = Date.parse(status.deadline) - serverNow;
    subscription.deadlineTimer = setTimeout(
      () => {
        subscription.deadlineTimer = null;
        refetchedDeadlineByCart.set(cartKey, status.deadline);
        refetchIfCurrent(cartKey, subscription);
      },
      Math.max(0, msToDeadline) + REFETCH_GRACE_MS,
    );
  }
  armMidnightIfIdle(cartKey, subscription);
}

/**
 * Subscribe to `cart`'s cut-off; returns the unsubscribe (safe to call twice). The last
 * unsubscribe tears the cart down one tick later unless a subscriber joins first.
 */
export function subscribeToCartCutOff(cart: CartAccount, now: () => number = Date.now): () => void {
  const cartKey = cartAccountKey(cart);
  let subscription = subscriptions.get(cartKey);
  if (subscription === undefined) {
    subscription = {
      cart: { depotId: cart.depotId, customerAccountCode: cart.customerAccountCode },
      subscriberCount: 0,
      now,
      controller: null,
      deadlineTimer: null,
      midnightTimer: null,
      retryTimer: null,
      teardownTimer: null,
      retriesUsed: 0,
      offsetMs: 0,
      failed: false,
    };
    subscriptions.set(cartKey, subscription);
    read(cartKey, subscription);
  } else {
    // A join within the tick after the last subscriber left keeps the subscription as it is.
    subscription.teardownTimer = clearTimer(subscription.teardownTimer);
    if (subscription.failed) read(cartKey, subscription);
  }
  subscription.subscriberCount += 1;
  const held = subscription;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    held.subscriberCount -= 1;
    if (held.subscriberCount > 0) return;
    held.teardownTimer = clearTimer(held.teardownTimer);
    held.teardownTimer = setTimeout(() => {
      held.teardownTimer = null;
      if (held.subscriberCount > 0 || !isCurrent(cartKey, held)) return;
      held.controller?.abort();
      disarm(held);
      subscriptions.delete(cartKey);
      setView(cartKey, null);
    }, 0);
  };
}
